import React from 'react';
import { Link } from 'react-router-dom';
import { FileSearch, Shield } from 'lucide-react';
import config from '../../config';

export interface PublicFooterProps {
  /** Custom CTA label (e.g. "Request Private Deployment") */
  ctaLabel?: string;
  /** Custom CTA link (e.g. "/contact" for Private AI page) */
  ctaTo?: string;
  /** Optional custom CTA element (e.g. button with onClick) */
  ctaElement?: React.ReactNode;
  /** Variant: "full" (default) shows all columns; "compact" shows only Legal + copyright for minimal pages */
  variant?: 'full' | 'compact';
}

const PublicFooter: React.FC<PublicFooterProps> = ({
  ctaLabel = 'Sign Up Free',
  ctaTo = '/register',
  ctaElement,
  variant = 'full',
}) => {
  const currentYear = new Date().getFullYear();

  if (variant === 'compact') {
    return (
      <footer className="bg-gray-900 text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <FileSearch className="h-6 w-6 text-primary-400 mr-2" />
              <span className="font-semibold">{config.appName}</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-gray-400 text-sm">
              <Link to="/terms" className="hover:text-white transition-colors">Terms and Conditions</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 pt-6 text-center text-gray-400 text-sm">
            <p>&copy; {currentYear} {config.appName}. All rights reserved.</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-900 text-white py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          <div>
            <div className="flex items-center mb-4">
              <FileSearch className="h-8 w-8 text-primary-400 mr-2" />
              <span className="text-xl font-bold">{config.appName}</span>
            </div>
            <p className="text-gray-400">
              AI-powered document intelligence platform for every industry. Transform your document
              workflow and drive results faster with intelligent automation.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link to="/how-to-use" className="hover:text-white transition-colors">How to Use</Link>
              </li>
              <li>
                <Link to="/use-cases" className="hover:text-white transition-colors">Use Cases</Link>
              </li>
              <li>
                <Link to="/private-ai" className="hover:text-white transition-colors">Private AI/LLM</Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link to="/terms" className="hover:text-white transition-colors">Terms and Conditions</Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Get Started</h4>
            {ctaElement != null ? (
              ctaElement
            ) : (
              <Link
                to={ctaTo}
                className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors mb-4"
              >
                {ctaLabel}
              </Link>
            )}
            {!ctaElement && (
              <p className="text-gray-400 text-sm">
                Join teams who are already using document intelligence to automate their document work and drive better results.
              </p>
            )}
          </div>
        </div>
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; {currentYear} {config.appName}. All rights reserved. Document intelligence for every industry.</p>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
